import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSystem } from "../../../../../http/patrol/types/patrolSystems";
import { AdminSupportExtra } from "../../../../../http/patrol/types/supportExtras";
import {
  AdminSupportOptional,
  InsertSupportOptionalBody,
  UpdateSupportOptionalBody,
} from "../../../../../http/patrol/types/supportOptionals";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { useSupportExtrasApi } from "../../../../../http/patrol/useSupportExtras";
import { useSupportOptionalsApi } from "../../../../../http/patrol/useSupportOptionals";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

type optionalTypeValues = "only-life-line" | "only-anchor-point" | "both";

type optionalType = {
  value: optionalTypeValues;
  title: string;
};

interface SupportOptionalFormBody {
  item: AdminItem | null;
  system: AdminPatrolSystem | null;
  supportExtraDependency: AdminSupportExtra | null;
  type: optionalTypeValues;
  factor: number | null;
  visibility: VisibilityScopeValue;
}

interface SupportOptionalFormProps {
  defaultValues?: AdminSupportOptional;
  onClose: () => void;
}

const SupportOptionalForm: FC<SupportOptionalFormProps> = ({ defaultValues, onClose }: SupportOptionalFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const supportExtrasApi = useSupportExtrasApi();
  const supportOptionalsApi = useSupportOptionalsApi();
  const patrolSystemsApi = usePatrolSystemsApi();
  const itemsApi = useItemsApi();

  const patrolSystemsResult = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);
  const supportExtraResult = supportExtrasApi.useAdminSupportExtras({}, aborter.current.signal, patrolSystemId);

  const optionalTypes: optionalType[] = [
    {
      value: "only-life-line",
      title: i18n.translation.common.onlyLifeLine,
    },
    {
      value: "only-anchor-point",
      title: i18n.translation.common.onlyAnchorPoint,
    },
    {
      value: "both",
      title: i18n.translation.common.lifeLinePlusAnchorPoint,
    },
  ];

  const getType = (forAnchorPoints: boolean | undefined, forLifeLines: boolean | undefined): optionalTypeValues =>
    forAnchorPoints && forLifeLines
      ? "both"
      : forAnchorPoints
        ? "only-anchor-point"
        : forLifeLines
          ? "only-life-line"
          : "both";

  return (
    <Form<SupportOptionalFormBody>
      defaultValues={{
        ...defaultValues,
        factor: defaultValues?.factor ?? 1,
        type: getType(defaultValues?.forAnchorPoints, defaultValues?.forLifeLines),
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const update: UpdateSupportOptionalBody = {
          systemId: patrolSystemId ?? formData.system?.id ?? "",
          supportExtraDependencyId: formData.supportExtraDependency?.id,
          forLifeLines: formData.type === "only-life-line" || formData.type === "both",
          forAnchorPoints: formData.type === "only-anchor-point" || formData.type === "both",
          factor: formData.factor ?? 1,
          visibility: formData.visibility,
        };

        if (defaultValues?.id) {
          try {
            await supportOptionalsApi.updateAdminSupportOptional(defaultValues.id, update);
            onClose();
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          const insert: InsertSupportOptionalBody = {
            ...update,
            itemId: formData.item?.id!,
          };

          try {
            await supportOptionalsApi.insertAdminSupportOptional(insert);
            onClose();
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, watch }) => {
        const supportExtraDependency = watch("supportExtraDependency");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isDisabled={isSubmitting || !!defaultValues?.id}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="supportExtraDependency"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.onlyFor} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminSupportExtra>
                      {...field}
                      inputId={labelId}
                      options={supportExtraResult.data?.items ?? []}
                      isDisabled={!!supportExtraResult.error || isSubmitting || !supportExtraResult.data?.items.length}
                      isLoading={supportExtraResult.isLoading}
                      isClearable
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.item.code}
                      onChange={(value) => {
                        if (value) {
                          setValue("type", getType(value.forAnchorPoints, value.forLifeLines));
                          setValue("factor", value.factor);
                        }
                        setValue("supportExtraDependency", value);
                      }}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="system"
              control={control}
              rules={{ required: !patrolSystemId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.patrolSystems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminPatrolSystem>
                      {...field}
                      inputId={labelId}
                      options={patrolSystemsResult.data?.items ?? []}
                      isDisabled={!!patrolSystemsResult.error || isSubmitting}
                      isLoading={patrolSystemsResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<optionalType>
                      {...field}
                      inputId={labelId}
                      options={optionalTypes}
                      isDisabled={isSubmitting || !!supportExtraDependency}
                      isLoading={isSubmitting}
                      getOptionValue={(x) => x.value}
                      getOptionLabel={(x) => x.title}
                      value={optionalTypes.find((option) => option.value === field.value) ?? null}
                      onChange={(option) =>
                        setValue("type", optionalTypes.find((s) => s.value === option?.value)?.value!)
                      }
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="factor"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.factor} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting || !!supportExtraDependency}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("factor", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupportOptionalForm;
